import {ChangeEvent, useRef, useState} from "react";
import {Box, IconButton, ListItemIcon, Menu, MenuItem, Tooltip} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import {alpha} from "@mui/material/styles";

type Props = {
    accept: string;
    disabled: boolean;
    onFilesSelected: (files: FileList | null) => void;
    openMenuLabel: string;
    uploadFileLabel: string;
};

const AttachmentMenuButton: React.FC<Props> = ({
                                                   accept,
                                                   disabled,
                                                   onFilesSelected,
                                                   openMenuLabel,
                                                   uploadFileLabel,
                                               }) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleUploadFileClick = () => {
        handleMenuClose();
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        onFilesSelected(event.target.files);
        event.target.value = "";
    };

    return (
        <Box sx={{display: "flex", alignItems: "center"}}>
            <input
                accept={accept}
                aria-hidden
                multiple
                onChange={handleFileInputChange}
                ref={fileInputRef}
                style={{display: "none"}}
                type="file"
            />
            <Tooltip title={openMenuLabel}>
                <span>
                    <IconButton
                        aria-label={openMenuLabel}
                        disabled={disabled}
                        onClick={handleMenuOpen}
                        size="large"
                        sx={(theme) => ({
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.14),
                            },
                        })}
                    >
                        <AddRoundedIcon/>
                    </IconButton>
                </span>
            </Tooltip>
            <Menu
                anchorEl={menuAnchor}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                onClose={handleMenuClose}
                open={Boolean(menuAnchor)}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <MenuItem onClick={handleUploadFileClick}>
                    <ListItemIcon
                        sx={(theme) => ({color: theme.palette.mode === "dark" ? theme.palette.common.white : "inherit"})}>
                        <UploadFileRoundedIcon
                            fontSize="small"
                            sx={(theme) => ({color: theme.palette.mode === "dark" ? theme.palette.common.white : "inherit"})}
                        />
                    </ListItemIcon>
                    {uploadFileLabel}
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default AttachmentMenuButton;
